import React, { useEffect, useState } from 'react';
import proptypes from 'prop-types';
const { func, number, bool, arrayOf, shape, string } = proptypes;
import { extend } from '@thd-nucleus/data-sources';
import { DataGrid } from '@thd-olt-component-react/data-grid';
import { MoreButton } from '@thd-olt-component-react/project-forms';
import { TableFooterShowMore, TableFooterPagination } from '@one-thd/sui-atomic-components';
import {
  PROJECTS_PAGINATION,
  INITIAL_TABLE_VIEW_PAGE_SIZE,
  SORT_KEY_LAST_MODIFIED,
  TABLE_VIEW_TYPE
} from './project-summary-helper';
import { dateModifiedMessage, triggerAnalytics, sortAndFilterAnalytics } from '../helpers';
import { createColumns } from './utils/columnsCreator.utility';
import { EmptyStateMessage } from './EmptyStateMessage/EmptyStateMessage';

// Helper functions extracted to reduce complexity
const formatProjectData = (projects) => {
  if (!projects || !projects.length) return [];

  return projects.map((project) => ({
    id: project.id,
    projectName: project.projectName,
    projectNumber: project.projectId,
    projectStatus: project.status,
    lastModified: dateModifiedMessage(project.modifyDateTime),
    createdBy: project.createdByName,
    dropdown: '',
    isCreatedByTHDAssociate: project.isCreatedByTHDAssociate,
    url: `projects/${project.id}`
  }));
};

// Extracted pagination footer component
// eslint-disable-next-line react/prop-types
const PaginationFooter = ({ totalResults, page, onPageChange, onPageSizeChange }) => {
  // Different pagination types based on result count
  if (totalResults > 25) {
    return (
      <TableFooterPagination
        page={page - 1}
        count={totalResults}
        rowsPerPage={INITIAL_TABLE_VIEW_PAGE_SIZE}
        onPageChange={onPageChange}
        onRowsPerPageChange={onPageSizeChange}
        disableViewFifty
        disableViewAll
        DropdownProps={{
          MenuProps: { autoFocus: false }
        }}
      />
    );
  }

  if (totalResults > 10) {
    return (
      <TableFooterShowMore
        rowsPerPage={INITIAL_TABLE_VIEW_PAGE_SIZE}
        onRowsPerPageChange={onPageSizeChange}
        count={totalResults}
      />
    );
  }

  return null;
};

export const TableView = ({
  projects, loading, page, onPageChange, onPageSizeChange, totalResults, setSortCriteria, sortCriteria, filterCriteria
}) => {
  const [data, setData] = useState([]);
  const [visibleRows, setVisibleRows] = useState(INITIAL_TABLE_VIEW_PAGE_SIZE);

  // Format project data when projects or loading state changes
  useEffect(() => {
    if (!projects || loading) {
      return;
    }
    setData(formatProjectData(projects));
  }, [projects, loading]);

  // Page size change handlers
  const handleRowsPerPageChange = (_event, rows) => {
    onPageChange(1);
    onPageSizeChange(rows);
    setVisibleRows(rows);
  };

  const handleShowMoreRows = (_event, rows) => {
    onPageSizeChange(rows);
    setVisibleRows(rows);
  };

  // Page navigation handler
  const handlePageChange = (_event, newPage) => {
    triggerAnalytics(PROJECTS_PAGINATION, {
      paginationType: 'projects: projects page: table view: pagination'
    });
    onPageChange(newPage + 1);
  };

  // Get current sort value for a column
  const getColumnSortValue = (columnKey) => {
    // First check if column is already being sorted
    const existingSortValue = sortCriteria.find((criteria) => criteria.sortBy === columnKey);

    if (existingSortValue) {
      return existingSortValue.sortOrder === 'ASC' ? 'asc' : 'desc';
    }

    // Default sort for LAST_MODIFIED when no other sorting is applied
    if (sortCriteria.length === 0 && columnKey === SORT_KEY_LAST_MODIFIED) {
      return 'asc';
    }

    return null;
  };

  // Get next sort order in the cycle: null -> asc -> desc -> null
  const getNextSort = (sortBy) => {
    if (!sortBy) return 'ASC';
    if (sortBy === 'asc') return 'DSC';
    return null; // Reset sort if currently desc
  };

  // Handle column sorting
  const handleSort = (_event, column, sortBy) => {
    // Skip sort for last modified column which already has default sort
    if (column.key === SORT_KEY_LAST_MODIFIED) return;

    const nextSortOrder = getNextSort(sortBy);

    if (nextSortOrder) {
      setSortCriteria([{ sortBy: column.key, sortOrder: nextSortOrder }]);
    } else {
      setSortCriteria([]);
    }

    onPageChange(1); // Reset to first page when sorting changes
  };

  // Track analytics when sort criteria change
  useEffect(() => {
    if (sortCriteria.length || filterCriteria) {
      sortAndFilterAnalytics({
        sortCriteria,
        filterCriteria,
        viewType: TABLE_VIEW_TYPE
      });
    }
  }, [sortCriteria, filterCriteria]);

  // Show empty state if no data to show
  if ((!projects || projects.length === 0) && !loading) {
    return (
      <div>
        <DataGrid
          columns={createColumns(getColumnSortValue)}
          data={[]}
          layout="auto"
          dataId="id"
          loading={false}
          onSort={handleSort}
        />
        <EmptyStateMessage />
      </div>
    );
  }

  // Create columns with current sort values
  const columns = createColumns(getColumnSortValue);

  return (
    <DataGrid
      columns={columns}
      data={data.slice(0, visibleRows)}
      layout="auto"
      dataId="id"
      loading={loading}
      onSort={handleSort}
      skeletonRowCount={visibleRows}
      footer={(
        <div data-testid="projects-table-pagination">
          <PaginationFooter
            totalResults={totalResults}
            page={page}
            onPageChange={handlePageChange}
            onPageSizeChange={totalResults > 25 ? handleRowsPerPageChange : handleShowMoreRows}
          />
        </div>
      )}
    />
  );
};

TableView.propTypes = {
  projects: arrayOf(shape({})),
  totalResults: number,
  page: number,
  onPageChange: func,
  onPageSizeChange: func,
  loading: bool,
  setSortCriteria: func,
  sortCriteria: arrayOf(shape({})),
  filterCriteria: shape({
    projectName: string,
    projectNumber: string,
    projectStatus: string
  })
};

TableView.defaultProps = {
  projects: [],
  totalResults: 0,
  page: 1,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  loading: false,
  setSortCriteria: () => {},
  sortCriteria: [],
  filterCriteria: {
    projectName: '',
    projectNumber: '',
    projectStatus: ''
  }
};

TableView.dataModel = extend(
  MoreButton
);
